import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { NgForm } from '@angular/forms';

import { find, get, isArray } from 'lodash';

import { ISite, SitesService, UserService } from '@nrwl/common-lib';

import { IValueChangedEvent, IWizardContentComponent } from '../../../wizard.content';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import * as sitesUploadLayout from '../json/sites.upload.json';
import * as sitesAlternateUploadLayout from '../json/sites.alternate.upload.json';

@Component({
  selector: 'sites-desc-html',
  templateUrl: './sites.desc.html',
  styleUrls: ['../wizard.sites.style.scss', './sites.desc.style.scss']
})
export class SitesDescHtmlComponent
  implements IWizardContentComponent, OnInit, OnDestroy, IWizardContentComponent
{
  @Input() step: any; //eslint-disable-line
  @Input() subStep: any; //eslint-disable-line
  @Input() wizardCtrl: any; //eslint-disable-line
  @Input() componentId: string;
  @Input() previousOutput: any; //eslint-disable-line
  //eslint-disable-next-line
  @Output() onDataValueChange = new EventEmitter<IValueChangedEvent>();

  @ViewChild('currentForm') form: NgForm;

  uploadDataSource: any; //eslint-disable-line
  uploadAlternateDataSource: any; //eslint-disable-line
  showInput = false;
  selectedRowIndex = 0;
  wizardService: any; //eslint-disable-line
  isLoading = false;
  sites: ISite[];
  names: string[];
  isApexSyncActive = false;

  private readonly unsubscribe: Subject<void> = new Subject();

  constructor(
    private readonly sitesService: SitesService,
    private readonly userService: UserService
  ) {
    this.uploadDataSource = sitesUploadLayout;
    this.uploadAlternateDataSource = sitesAlternateUploadLayout;
  }

  ngOnInit() {
    if (this.userService.isGigatestUser) {
      this.fetchApexInfo();
    }

    this.step.form = this.form;
    this.wizardService = this.wizardCtrl.wizardService;

    this.sites = this.sitesService.getActiveSites();
    this.names = this.sites.map(s => s.name).filter(name => !!name);

    // attempt populate inputs, in case user came here by pressing 'back'
    if (this.wizardService.editMode) {
      this.fillFormData();
    }

    this.sitesService.isManualMethodType
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((value: boolean) => {
        if (value) {
          const manualSite = {
            id: 'mannual',
            iconSrc: 'images/site.svg',
            label: 'Add a site manually',
            description: 'mata'
          };
          this.step.onItemClick('creationType', manualSite);
          this.sitesService.isManualMethodType.next(false);
        }
      });
  }

  fetchApexInfo(): void {
    this.isLoading = true;
    this.sitesService
      .getAllApexSync()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        response => {
          this.isLoading = false;
          this.isApexSyncActive = response.some(data => data.isActive);
        },
        () => {
          this.isLoading = false;
          this.isApexSyncActive = false;
        }
      );
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  fillFormData(): void {
    this.step.output.name = get(this.previousOutput, 'name');
    this.step.output.description = get(this.previousOutput, 'description', '');
    this.step.output.tags = get(this.previousOutput, 'tags', '');
    this.names = this.names.filter(name => name !== this.step.output.name);
    this.step.output.location = get(this.previousOutput, 'location', {});
    this.step.output.subnets = get(this.previousOutput, 'subnets', []);
  }

  //eslint-disable-next-line
  csvRecordsEvent(dataObj: any): any {
    const jsonData = dataObj.jsonData;
    this.wizardService.uploadDataObj = dataObj;
    this.wizardService.formattedSiteData = this.populateSiteJsonData(jsonData);
    this.wizardCtrl.toggleNext();
  }

  //eslint-disable-next-line
  populateSiteJsonData(csvData: any): any {
    const dataArray = [];
    if (csvData && isArray(csvData)) {
      let result: any = {}; //eslint-disable-line
      csvData.forEach(dataSets => {
        let siteDataExist = find(dataArray, ['name', dataSets.site]);
        if (!siteDataExist) {
          siteDataExist = find(dataArray, ['name', dataSets.Site]);
        }

        if (siteDataExist) {
          siteDataExist.subnets.push(this.getSubnets(dataSets));
        } else {
          result = {
            name: dataSets.site || dataSets.Site,
            locationName: this.populateLocationName(dataSets),
            location: this.getLocation(dataSets),
            subnets: [this.getSubnets(dataSets)],
            source: null,
            tags: [dataSets.tags]
          };
          dataArray.push(result);
        }
      });
    }
    return dataArray;
  }

  //eslint-disable-next-line
  getLocation(dataSets: any): any {
    let result = {};
    result = {
      lat: dataSets.latitude || dataSets.Latitude,
      lon: dataSets.longitude || dataSets.Longitude,
      country: dataSets.country,
      city: dataSets.city,
      state: dataSets.state,
      location: dataSets.Location
    };
    return result;
  }

  //eslint-disable-next-line
  populateLocationName(dataSet: any): any {
    let locationFullName = '';
    if (location) {
      locationFullName += dataSet.city ? dataSet.city + ',' : '';
      locationFullName += dataSet.state ? dataSet.state + ',' : '';
      locationFullName += dataSet.country ? dataSet.country : '';
    }
    return dataSet.Location || locationFullName;
  }

  //eslint-disable-next-line
  getSubnets(dataSets: any): any {
    let result = {};
    result = {
      name: dataSets.subnetname || dataSets['Subnet Name'],
      description: dataSets.subnetdescription,
      subnet: dataSets.subnetcidr || dataSets['Subnet/CIDR']
    };
    return result;
  }

  nameUpdated(): void {
    this.step.output.name = this.step.output.name.trim();
    this.onDataValueChange.emit({
      componentId: this.componentId,
      inputId: 'nameInput',
      key: 'step.output.name',
      newValue: this.step.output.name,
      isValid: this.form.controls.name.valid
    });
  }
}
